import type { GrantRecord } from '@fruuit/shared'
import { Badge } from '@fruuit/ui'
import { formatDate } from '../feeds/format'

/** Current access state for a subscription or entitlement. */
export function GrantState({
  grant,
  activeLabel,
  inactiveLabel,
}: {
  grant: GrantRecord | null
  activeLabel: string
  inactiveLabel: string
}) {
  if (!grant) return <Badge tone="quiet">{inactiveLabel}</Badge>
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone="positive" dot>
        {activeLabel}
      </Badge>
      <span className="text-xs leading-relaxed text-sage">
        Since {formatDate(grant.granted_at, '—')}
        {grant.granted_by_name ? ` · ${grant.granted_by_name}` : ''}
      </span>
    </div>
  )
}

/** Append-only grant history (P-CAN-005, R-07): every grant and revocation with its reason. */
export function GrantHistory({ history }: { history: readonly GrantRecord[] }) {
  if (history.length === 0) return null
  return (
    <details className="group mt-2 text-xs">
      <summary className="cursor-pointer font-semibold text-teal hover:underline">
        History ({history.length})
      </summary>
      <ol className="mt-2 flex flex-col gap-2 border-l-2 border-line pl-3">
        {history.map((record) => (
          <li key={record.id} className="leading-relaxed text-ink-soft">
            <p>
              <span className="font-semibold text-ink">Granted</span>{' '}
              {formatDate(record.granted_at, '—')}
              {record.granted_by_name ? ` by ${record.granted_by_name}` : ''}: {record.reason}
            </p>
            {record.revoked_at ? (
              <p>
                <span className="font-semibold text-ink">Revoked</span>{' '}
                {formatDate(record.revoked_at, '—')}
                {record.revoked_by_name ? ` by ${record.revoked_by_name}` : ''}
                {record.revoke_reason ? `: ${record.revoke_reason}` : ''}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </details>
  )
}
